import type {BetRecordDTO, BetRecordQry, GameRuleDTO, PlaceBetCmd} from '../types/betting'
import { ApiClient, endpoints } from './request'
import type { SingleResponse, MultiResponse, PageResponse } from './request'
import { isSingleResponse, isMultiResponse, isPageResponse } from './request'
import { logger } from '../config'

// 提交投注
export async function placeBet(betData: PlaceBetCmd): Promise<any> {
  try {
    logger.debug('提交投注:', betData)
    
    const response: SingleResponse<any> = await ApiClient.post(
      endpoints.game.placeBet,
      betData
    )
    
    if (response.success) {
      return response.data
    } else {
      throw new Error(response.errMsg || '投注失敗')
    }
    
  } catch (error) {
    logger.error('投注請求失敗:', error)
    throw error
  }
}

// 獲取當前期數
export async function getCurrentPeriod(): Promise<{ periodId: number; periodNo: string; canBet: boolean; endTime: number}> {
  try {
    logger.debug('獲取當前期數')
    
    const response: SingleResponse<{ periodId: number; periodNo: string; canBet: boolean; endTime: number }> = await ApiClient.get(
      `${endpoints.game.currentPeriod}/fast3`
    )
    
    if (response.success && response.data) {
      return response.data
    } else {
      throw new Error(response.errMsg || '獲取期數失敗')
    }
    
  } catch (error) {
    logger.error('獲取期數失敗:', error)
    throw error
  }
}

// 獲取用戶餘額
export async function getUserBalance(): Promise<number> {
  try {
    const response: SingleResponse<{
      availableBalance: number }> = await ApiClient.get(
      endpoints.user.balance
    )
    if (response.success && response.data?.availableBalance !== undefined) {
      return response.data.availableBalance
    } else {
      return -1
    }
    
  } catch (error) {
    logger.error('獲取餘額失敗:', error)
    throw error
  }
}


export async function getLatestDrawResult(gameType: string): Promise<{
  periodNo: string
  dice1: number
  dice2: number
  dice3: number
  sum: number
}> {
  try {
    logger.debug('獲取上期開獎結果')

    const response: SingleResponse<{
      periodNo: string
      dice1: number
      dice2: number
      dice3: number
      sum: number
    }> = await ApiClient.get(
        `${endpoints.game.gameResult}/${gameType}`
    )

    if (response.success && response.data) {
      return response.data
    } else {
      throw new Error(response.errMsg || '獲取上期開獎結果失敗')
    }

  } catch (error) {
    logger.error('獲取上期開獎結果失敗:', error)
    throw error
  }
}

export async function getGameRules(gameType: string): Promise<GameRuleDTO[]> {
  try {
    const response: MultiResponse<GameRuleDTO> = await ApiClient.getList(
        `${endpoints.game.gameRules}/${gameType}`
    )
    if (response.success && response.data) {
      return response.data
    } else {
      throw new Error(response.errMsg || '獲取遊戲規則失敗')
    }

  } catch (error) {
    logger.error('獲取遊戲規則失敗:', error)
    return [] // 返回空數組作為fallback
  }
}

// 獲取投注歷史記錄 - 多條數據示例
export async function getBetHistory(userId: string): Promise<any[]> {
  try {
    logger.debug('獲取投注歷史記錄:', userId)
    
    const response: MultiResponse<any> = await ApiClient.getList(
      `${endpoints.game.betHistory}?userId=${userId}`
    )
    
    if (response.success) {
      logger.log('獲取投注歷史成功:', response.data.length)
      return response.data
    } else {
      throw new Error(response.errMsg || '獲取投注歷史失敗')
    }
    
  } catch (error) {
    logger.error('獲取投注歷史失敗:', error)
    return []
  }
}

// 獲取投注歷史記錄（分頁）- 分頁數據示例
export async function getBetHistoryPage(userId: string, page: number = 1, size: number = 10): Promise<{
  data: any[]
  total: number
  currentPage: number
  pageSize: number
}> {
  try {
    logger.debug('獲取投注歷史記錄（分頁）:', { userId, page, size })
    
    const response: PageResponse<any> = await ApiClient.getPage(
      `${endpoints.game.betHistory}?userId=${userId}&page=${page}&size=${size}`
    )
    
    if (response.success) {
      logger.log('獲取投注歷史分頁成功:', { 
        dataCount: response.data.length,
        totalCount: response.total
      })
      return {
        data: response.data,
        total: response.total,
        currentPage: page,
        pageSize: size
      }
    } else {
      throw new Error(response.errMsg || '獲取投注歷史分頁失敗')
    }
    
  } catch (error) {
    logger.error('獲取投注歷史分頁失敗:', error)
    return {
      data: [],
      total: 0,
      currentPage: page,
      pageSize: size
    }
  }
}

// 自動判斷響應類型的示例函數
export async function getGameData(gameType: string): Promise<{
  type: 'single' | 'multi' | 'page'
  data: any
}> {
  try {
    logger.debug('獲取遊戲數據:', gameType)
    
    const response = await ApiClient.getAuto(
      `${endpoints.game.gameResult}?gameType=${gameType}`
    )
    
    if (response.success) {
      if (isSingleResponse<any>(response)) {
        const singleResp = response as SingleResponse<any>
        logger.log('獲取單條遊戲數據成功')
        return { type: 'single', data: singleResp.data }
      } else if (isMultiResponse<any>(response)) {
        const multiResp = response as MultiResponse<any>
        logger.log('獲取多條遊戲數據成功:', multiResp.data.length)
        return { type: 'multi', data: multiResp.data }
      } else if (isPageResponse<any>(response)) {
        const pageResp = response as PageResponse<any>
        logger.log('獲取分頁遊戲數據成功:', { 
          dataCount: pageResp.data.length,
          totalCount: pageResp.total
        })
        return { 
          type: 'page', 
          data: { 
            list: pageResp.data, 
            totalCount: pageResp.total
          } 
        }
      }
    }
    
    throw new Error(response.errMsg || '獲取遊戲數據失敗')
    
  } catch (error) {
    logger.error('獲取遊戲數據失敗:', error)
    throw error
  }
}

// 获取用户投注记录（分页）
export async function getBetRecords(params: BetRecordQry = {}): Promise<{
  data: BetRecordDTO[]
  total: number
  pageIndex: number
  pageSize: number
  hasMore: boolean
}> {
  try {
    logger.debug('获取投注记录:', params)

    // 默认参数
    const defaultParams = {
      pageIndex: 1,
      pageSize: 3,
      ...params
    }

    // 构建查询参数 - 简化写法
    const queryParams = new URLSearchParams()

    // 只添加有值的参数
    Object.entries(defaultParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value.toString())
      }
    })

    const url = `${endpoints.game.betRecords}?${queryParams.toString()}`

    const response: PageResponse<BetRecordDTO> = await ApiClient.getPage(url, {
      skipErrorHandler: true // 🔧 临时跳过错误处理
    })

    if (response.success) {
      const hasMore = (defaultParams.pageIndex * defaultParams.pageSize) < response.total
      return {
        data: response.data,
        total: response.total,
        pageIndex: defaultParams.pageIndex,
        pageSize: defaultParams.pageSize,
        hasMore
      }
    } else {
      throw new Error(response.errMsg || '获取投注记录失败')
    }

  } catch (error) {
    logger.error('获取投注记录失败:', error)
    return {
      data: [],
      total: 0,
      pageIndex: params.pageIndex || 1,
      pageSize: params.pageSize || 3,
      hasMore: false
    }
  }
}